import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import FancyDropdown, {
  FancyDropdownMenu,
  FancyDropdownMenuItem
} from 'dpl/components/FancyDropdown';
import { isEmpty, get } from 'dpl/shared/utils/object';
import { groupBy } from 'dpl/shared/utils';
import ErrorWrapper from 'dpl/form/components/ErrorWrapper';
import Icon from 'dpl/common/components/Icon';

export function getMatchingLocations(matchText, availableBreeds) {
  if (matchText) {
    matchText = matchText.toLowerCase();
    availableBreeds = availableBreeds.filter(b =>
      b.name.toLowerCase().includes(matchText)
    );
  }

  return availableBreeds;
}

export default function LocationSelect({
  availableLocations,
  className,
  displayErrors,
  errors,
  name,
  id,
  omitIcon,
  disablePlaceholder,
  onSelect,
  selectedId,
  defaultIsOpen,
  closeDropdown
}) {
  const [textInput, setTextInput] = useState(null);
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const [locationsById, setLocationsById] = useState({});
  const [locationDropdownOptions, setLocationDropdownOptions] = useState([]);
  const [locationsByNames, setLocationsByNames] = useState({});
  const [selectedLocationId, setSelectedLocationId] = useState(selectedId);

  const inputRef = useRef();

  const selectedLocationName = get(
    locationsById[selectedLocationId],
    '0.name',
    ''
  );

  if (selectedId !== selectedLocationId) {
    setSelectedLocationId(selectedId);
  }

  useEffect(() => {
    if (isEmpty(locationsById)) {
      setLocationsById(groupBy(availableLocations, 'id'));
      setLocationsByNames(groupBy(availableLocations, 'name'));
    }
  }, [availableLocations]);

  function locationById(locationId) {
    return get(locationsById, `${locationId}`, [])[0];
  }
  function locationByName(locationName) {
    return get(locationsByNames, `${locationName}`, [])[0];
  }

  function handleInputBlur() {
    const textInputLocation = locationByName(selectedLocationName);
    const selectedLocation = locationById(selectedLocationId);
    setTextInput(null);

    // If we have a text input location, use that
    if (textInputLocation) {
      if (!selectedLocation || textInputLocation.id !== selectedLocation.id) {
        setSelectedLocationId(textInputLocation.id);
        onSelect(textInputLocation.id);
      }
      closeDropdown && closeDropdown();
      return;
    }

    // No text input location but we have a selected location
    if (selectedLocation) {
      if (disablePlaceholder) {
        setSelectedLocationId(selectedLocation.name);
      } else {
        setSelectedLocationId(null);
        onSelect(null);
      }
    }

    closeDropdown && closeDropdown();
  }

  function handleClickOption(locationId) {
    return event => {
      event.preventDefault();
      inputRef.current.blur();
      onSelect(locationId);
    };
  }

  function handleInputChange(event) {
    const locationText = event.target.value;
    setTextInput(locationText);
    setLocationDropdownOptions(
      getMatchingLocations(locationText, availableLocations)
    );
  }

  function handleDropdownChange(isDropdownOpen) {
    setIsOpen(isDropdownOpen);
  }

  function handleInputFocus(event) {
    window.setTimeout(() => event.target.select(), 1);

    setLocationDropdownOptions(
      getMatchingLocations(selectedLocationName, availableLocations)
    );
  }

  return (
    <div className="LocationSelect">
      <FancyDropdown onChange={handleDropdownChange} isOpen={isOpen}>
        <ErrorWrapper errors={errors} isShown={displayErrors}>
          <div className="w-100">
            <input
              ref={inputRef}
              className={classnames(
                'ba b--light-gray w-100 br3 pv3 ph4 font-16',
                className,
                {
                  'br--top': isOpen
                }
              )}
              name={name}
              autoComplete="off"
              type="text"
              value={
                typeof textInput === 'string' ? textInput : selectedLocationName
              }
              placeholder="Search location"
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              onChange={handleInputChange}
              autoFocus={defaultIsOpen}
              disabled={availableLocations.length === 0}
              id={id}
              data-test-id="location_select_input"
            />
            {!omitIcon && (
              <Icon
                className="mr2 pointer-events-none top-0 right-0 transform-center"
                name="fat-arrow-down"
              />
            )}
          </div>
        </ErrorWrapper>
        <div role="presentation" onMouseDown={e => e.preventDefault()}>
          <FancyDropdownMenu className="LocationSelect__dropdownMenu ba b--light-gray br2 br--bottom">
            {locationDropdownOptions.map(location => (
              <FancyDropdownMenuItem key={location.id} itemKey={location.id}>
                <button
                  data-test-id={`location_select_${location.id}`}
                  type="button"
                  onClick={handleClickOption(location.id)}
                  className="pv2 ph2 w-100"
                >
                  {location.name}
                </button>
              </FancyDropdownMenuItem>
            ))}
          </FancyDropdownMenu>
        </div>
      </FancyDropdown>
    </div>
  );
}

LocationSelect.propTypes = {
  disablePlaceholder: PropTypes.bool,
  availableLocations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      hybrid: PropTypes.bool
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // eslint-disable-next-line react/forbid-prop-types
  errors: PropTypes.array,
  displayErrors: PropTypes.bool,
  name: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  omitIcon: PropTypes.bool,
  defaultIsOpen: PropTypes.bool,
  closeDropdown: PropTypes.func
};

LocationSelect.defaultProps = {
  disablePlaceholder: false,
  selectedId: null,
  errors: [],
  displayErrors: false,
  name: 'location_name',
  className: null,
  id: null,
  omitIcon: false,
  defaultIsOpen: false,
  closeDropdown: null
};
